import type { JourneyRadarItem } from './journey-radar';
import type { WorkResponsibilityRow } from '../api';
import type { WorkInboxCounts, WorkInboxView } from './work-radar';

interface InboxUser {
  id?: string;
  email?: string;
}

export function responsibilityName(responsibility: WorkResponsibilityRow) {
  if (responsibility.teamHandle) return responsibility.teamHandle;
  return (
    responsibility.identity?.displayName ||
    responsibility.identity?.handle ||
    'Unmapped person'
  );
}

export function responsibilityAssignsWork(
  responsibility: WorkResponsibilityRow
) {
  return ['assignee', 'reviewer', 'approver', 'owner'].includes(
    responsibility.role
  );
}

function assignsWork(item: JourneyRadarItem) {
  return (
    item.nextAction?.responsibilities.some(responsibilityAssignsWork) || false
  );
}

function isAssignedToUser(item: JourneyRadarItem, user: InboxUser) {
  const action = item.nextAction;
  if (!action) return false;
  return (
    action.assignedToUserId === user.id ||
    action.assignedToEmail?.toLowerCase() === user.email?.toLowerCase() ||
    action.responsibilities.some(
      (responsibility) =>
        responsibilityAssignsWork(responsibility) &&
        responsibility.identity?.isCurrentUser
    )
  );
}

function belongsInView(
  item: JourneyRadarItem,
  view: WorkInboxView,
  user: InboxUser
) {
  const action = item.nextAction;
  if (!action) return false;
  if (view === 'mine') return isAssignedToUser(item, user);
  if (view === 'approvals')
    return (
      Boolean(action.policy) ||
      action.responsibilities.some((responsibility) =>
        ['reviewer', 'approver'].includes(responsibility.role)
      )
    );
  if (view === 'unowned')
    return (
      !action.assignedToUserId &&
      !action.assignedToEmail &&
      !action.requiredOwners.length &&
      !assignsWork(item)
    );
  return action.waitingOn === 'human' || action.waitingOn === 'system';
}

export function countWorkInboxItems(
  items: JourneyRadarItem[],
  user: InboxUser
): WorkInboxCounts {
  return items.reduce<WorkInboxCounts>(
    (counts, item) => {
      const action = item.nextAction;
      if (!action) return counts;
      if (isAssignedToUser(item, user)) counts.mine += 1;
      if (action.waitingOn === 'human' || action.waitingOn === 'system')
        counts.team += 1;
      if (
        action.policy ||
        action.responsibilities.some((responsibility) =>
          ['reviewer', 'approver'].includes(responsibility.role)
        )
      )
        counts.approvals += 1;
      if (
        !action.assignedToUserId &&
        !action.assignedToEmail &&
        !action.requiredOwners.length &&
        !assignsWork(item)
      )
        counts.unowned += 1;
      return counts;
    },
    { mine: 0, team: 0, approvals: 0, unowned: 0 }
  );
}

export function selectWorkInboxItems(
  items: JourneyRadarItem[],
  view: WorkInboxView,
  user: InboxUser
) {
  const severity = { critical: 0, warning: 1, info: 2 };
  const category = {
    failure: 0,
    missing_evidence: 1,
    human_action: 2,
    divergence: 3,
    stale: 4,
    external_wait: 5
  };
  return items
    .filter((item) => belongsInView(item, view, user))
    .slice()
    .sort((left, right) => {
      const a = left.nextAction;
      const b = right.nextAction;
      if (!a || !b) return a ? -1 : b ? 1 : 0;
      return (
        severity[a.severity] - severity[b.severity] ||
        category[a.category] - category[b.category] ||
        a.firstDetectedAt.localeCompare(b.firstDetectedAt)
      );
    });
}
